import { Button } from "@paribus/shared-ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { formatDate } from "../../../utils/dateUtils";
import type { VisitHeaderSaveValues } from "./VisitHeaderForm.types";
import { VisitsBulkEditModal } from "./VisitsBulkEditModal";
import { VisitsChronologyToolbar } from "./VisitsChronologyToolbar";
import { VisitsTable } from "./VisitsTable";
import { VisitsVirtualizedList } from "./VisitsVirtualizedList";
import { useVisitBulkSelection } from "./hooks/useVisitBulkSelection";
import { useVisitsChronologyFlatList } from "./hooks/useVisitsChronologyFlatList";
import {
  CaseFileOption,
  ChronologySortField,
  ChronologyViewType,
  EditingVisitDetailState,
  MedChronTemplate,
  SortDirection,
  VisitCitationClickHandler,
  VisitDisplay,
  VisitFieldSuggestions,
  VisitTypeFilterOption,
} from "./types";
import type { BulkEditSaveValues } from "./types/bulkEdit";
import { formatDetailCategory, formatVisitType } from "./utils/visitFormatting";
import { showVisitTypes } from "./utils/visitTypeFilter";

interface VisitsViewProps {
  isVisitsLoading?: boolean;
  visits: VisitDisplay[];
  viewType: ChronologyViewType;
  setViewType: (v: ChronologyViewType) => void;
  chronologySortField: ChronologySortField;
  chronologySortDirection: SortDirection;
  setChronologySort: (field: ChronologySortField, direction: SortDirection) => void;
  visitTypeFilterOptions: VisitTypeFilterOption[];
  hiddenVisitTypeIds: string[];
  setHiddenVisitTypeIds: (ids: string[]) => void;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  isEditModeAvailable: boolean;
  collapseTherapyVisits: boolean;
  setCollapseTherapyVisits: (v: boolean) => void;
  template: MedChronTemplate | null;
  fileMapping: Record<string, string>;
  onCitationClick: VisitCitationClickHandler;
  groupTherapyVisits: (list: VisitDisplay[], preserveOrder?: boolean) => VisitDisplay[];
  getVisitDetails: (visit: VisitDisplay) => MedVisitDetailResponse[];
  editingVisitDetail: EditingVisitDetailState | null;
  setEditingVisitDetail: (v: EditingVisitDetailState | null) => void;
  editingVisitHeader: string | null;
  newDetailCategory: string;
  setNewDetailCategory: (v: string) => void;
  newDetailText: string;
  setNewDetailText: (v: string) => void;
  showNewDetailForm: string | null;
  setShowNewDetailForm: (v: string | null) => void;
  isSavingDetail: boolean;
  setShowDeleteConfirm: (
    v: { versionId: string; visitId: string; category: string } | null
  ) => void;
  setShowDeleteVisitConfirm: (v: VisitDisplay | null) => void;
  handleVisitDetailUpdate: (
    visitId: string,
    oldCategory: string,
    newCategory: string,
    newDetails: string
  ) => void;
  handleSubmitNewDetail: (visitId: string) => void;
  handleVisitHeaderUpdate: (visitId: string, values: VisitHeaderSaveValues) => void;
  handleEditHeaderClick: (visit: VisitDisplay) => void;
  onCancelEditHeader: () => void;
  hasVisitTypeError: boolean;
  onClearVisitTypeError: () => void;
  autoResizeTextarea: (el: HTMLTextAreaElement | null) => void;
  availableFiles: CaseFileOption[];
  fieldSuggestions: VisitFieldSuggestions;
  onNewVisitClick: () => void;
  handleAddNewVisit: () => void;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  virtuosoRef?: React.RefObject<VirtuosoHandle | null>;
  onFlatItemsChange?: (getIndexForVisitId: (visitId: string) => number | null) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  highlightQuery: string;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  onRequestBulkDelete?: (visitIds: string[]) => void;
  onRequestBulkCopy?: (visitIds: string[]) => void;
  hasOtherVersions: boolean;
  onBulkEditSave: (visitIds: string[], values: BulkEditSaveValues) => Promise<boolean>;
  isBulkEditingVisits?: boolean;
  isBulkDeletingVisits?: boolean;
  isBulkCopyingVisits?: boolean;
  visitBulkSelectionRef?: React.MutableRefObject<{
    clearSelection: () => void;
    removeVisitIds: (ids: Iterable<string>) => void;
  } | null>;
  visitsDisplayMode: "list" | "table";
  setVisitsDisplayMode: (mode: "list" | "table") => void;
  targetVisitId?: string | null;
  onEditModeRowClick?: (visitId: string) => void;
  onFlashComplete?: () => void;
}

export const VisitsView: React.FC<VisitsViewProps> = ({
  isVisitsLoading,
  visits,
  viewType,
  setViewType,
  chronologySortField,
  chronologySortDirection,
  setChronologySort,
  visitTypeFilterOptions,
  hiddenVisitTypeIds,
  setHiddenVisitTypeIds,
  editMode,
  setEditMode,
  isEditModeAvailable,
  collapseTherapyVisits,
  setCollapseTherapyVisits,
  template,
  fileMapping,
  onCitationClick,
  groupTherapyVisits,
  getVisitDetails,
  editingVisitDetail,
  setEditingVisitDetail,
  editingVisitHeader,
  newDetailCategory,
  setNewDetailCategory,
  newDetailText,
  setNewDetailText,
  showNewDetailForm,
  setShowNewDetailForm,
  isSavingDetail,
  setShowDeleteConfirm,
  setShowDeleteVisitConfirm,
  handleVisitDetailUpdate,
  handleSubmitNewDetail,
  handleVisitHeaderUpdate,
  handleEditHeaderClick,
  onCancelEditHeader,
  hasVisitTypeError,
  onClearVisitTypeError,
  autoResizeTextarea,
  availableFiles,
  fieldSuggestions,
  onNewVisitClick,
  handleAddNewVisit: _handleAddNewVisit,
  scrollContainerRef,
  virtuosoRef,
  onFlatItemsChange,
  searchQuery,
  setSearchQuery,
  highlightQuery,
  searchInputRef,
  onRequestBulkDelete,
  onRequestBulkCopy,
  hasOtherVersions,
  onBulkEditSave,
  isBulkEditingVisits = false,
  isBulkDeletingVisits = false,
  isBulkCopyingVisits = false,
  visitBulkSelectionRef,
  visitsDisplayMode,
  setVisitsDisplayMode,
  targetVisitId,
  onEditModeRowClick,
  onFlashComplete,
}) => {
  const formatVisitTypeById = useCallback(
    (id: string) => formatVisitType(template, id),
    [template]
  );
  const formatDetailCategoryById = useCallback(
    (category: string, isCollapsed?: boolean) =>
      formatDetailCategory(template, category, isCollapsed),
    [template]
  );

  const isSearchActive = Boolean(highlightQuery?.trim());

  const { flatItems, visibleGroupedEntries, expandedGroups, setExpandedGroups } =
    useVisitsChronologyFlatList({
      visits,
      groupTherapyVisits,
      viewType,
      fileMapping,
      chronologySortField,
      chronologySortDirection,
      formatVisitType: formatVisitTypeById,
      onFlatItemsChange,
      preserveOrder: isSearchActive,
    });

  const groupVisitsByKey = useMemo(() => new Map(visibleGroupedEntries), [visibleGroupedEntries]);

  const {
    selectedVisitIds,
    clearSelection,
    removeVisitIds,
    toggleVisitRow,
    toggleGroup,
    visitRowCheckboxState,
    groupHeaderCheckboxState,
    selectedCount,
  } = useVisitBulkSelection(visits);

  useEffect(() => {
    if (!editMode) clearSelection();
  }, [editMode, clearSelection]);

  useEffect(() => {
    if (!visitBulkSelectionRef) return;
    visitBulkSelectionRef.current = { clearSelection, removeVisitIds };
    return () => {
      visitBulkSelectionRef.current = null;
    };
  }, [visitBulkSelectionRef, clearSelection, removeVisitIds]);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedVisitIds.size === 0) return;
    onRequestBulkDelete?.(Array.from(selectedVisitIds));
  }, [onRequestBulkDelete, selectedVisitIds]);

  const handleBulkCopyClick = useCallback(() => {
    if (selectedVisitIds.size === 0) return;
    onRequestBulkCopy?.(Array.from(selectedVisitIds));
  }, [onRequestBulkCopy, selectedVisitIds]);

  const bulkSelectedCount = editMode ? selectedCount : 0;
  const isBulkActionBusy = isBulkEditingVisits || isBulkDeletingVisits || isBulkCopyingVisits;
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  const handleBulkEditSave = useCallback(
    async (visitIds: string[], values: BulkEditSaveValues): Promise<boolean> => {
      const didSave = await onBulkEditSave(visitIds, values);
      if (didSave) {
        setShowBulkEditModal(false);
      }
      return didSave;
    },
    [onBulkEditSave]
  );

  const handleGroupHeaderBulkToggle = useCallback(
    (groupKey: string) => {
      const groupVisits = groupVisitsByKey.get(groupKey) ?? [];
      toggleGroup(groupVisits);
    },
    [groupVisitsByKey, toggleGroup]
  );

  const listedVisitTypeIds = visitTypeFilterOptions.map((option) => option.visitTypeId);
  // hiddenVisitTypeIds also holds other cases' types, so only this case's listed types count.
  const isEveryVisitHiddenByFilters =
    visits.length === 0 && listedVisitTypeIds.some((id) => hiddenVisitTypeIds.includes(id));

  return (
    <div className="space-y-1 sm:space-y-2">
      <VisitsChronologyToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        viewType={viewType}
        setViewType={setViewType}
        chronologySortField={chronologySortField}
        chronologySortDirection={chronologySortDirection}
        setChronologySort={setChronologySort}
        visitTypeFilterOptions={visitTypeFilterOptions}
        hiddenVisitTypeIds={hiddenVisitTypeIds}
        setHiddenVisitTypeIds={setHiddenVisitTypeIds}
        collapseTherapyVisits={collapseTherapyVisits}
        setCollapseTherapyVisits={setCollapseTherapyVisits}
        editMode={editMode}
        setEditMode={setEditMode}
        isEditModeAvailable={isEditModeAvailable}
        onCancelEditHeader={onCancelEditHeader}
        setEditingVisitDetail={setEditingVisitDetail}
        setShowNewDetailForm={setShowNewDetailForm}
        visitsDisplayMode={visitsDisplayMode}
        setVisitsDisplayMode={setVisitsDisplayMode}
        searchInputRef={searchInputRef}
      />
      {isEveryVisitHiddenByFilters ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <p className="text-sm text-muted-foreground">
            This case has visits, but none match the current filters.
          </p>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() =>
              setHiddenVisitTypeIds(showVisitTypes(hiddenVisitTypeIds, listedVisitTypeIds))
            }
          >
            Show all visit types
          </Button>
        </div>
      ) : visitsDisplayMode === "table" ? (
        <VisitsTable
          flatItems={flatItems}
          viewType={viewType}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          getVisitDetails={getVisitDetails}
          fileMapping={fileMapping}
          onCitationClick={onCitationClick}
          formatVisitType={formatVisitTypeById}
          formatDetailCategory={formatDetailCategoryById}
          formatDate={formatDate}
          highlightQuery={highlightQuery}
          editMode={editMode}
          onEditModeRowClick={onEditModeRowClick}
        />
      ) : (
        <VisitsVirtualizedList
          flatItems={flatItems}
          groupVisitsByKey={groupVisitsByKey}
          visitRowCheckboxState={visitRowCheckboxState}
          onVisitRowBulkToggle={toggleVisitRow}
          groupHeaderCheckboxState={groupHeaderCheckboxState}
          onGroupHeaderBulkToggle={handleGroupHeaderBulkToggle}
          selectedVisitCount={bulkSelectedCount}
          isBulkActionBusy={isBulkActionBusy}
          onBulkEditClick={() => setShowBulkEditModal(true)}
          onBulkCopyClick={handleBulkCopyClick}
          onBulkDeleteClick={handleBulkDeleteClick}
          hasOtherVersions={hasOtherVersions}
          isVisitsLoading={isVisitsLoading}
          virtuosoRef={virtuosoRef}
          scrollContainerRef={scrollContainerRef}
          viewType={viewType}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          getVisitDetails={getVisitDetails}
          onNewVisitClick={onNewVisitClick}
          editMode={editMode}
          fileMapping={fileMapping}
          onCitationClick={onCitationClick}
          editingVisitHeader={editingVisitHeader}
          editingVisitDetail={editingVisitDetail}
          setEditingVisitDetail={setEditingVisitDetail}
          newDetailCategory={newDetailCategory}
          setNewDetailCategory={setNewDetailCategory}
          newDetailText={newDetailText}
          setNewDetailText={setNewDetailText}
          showNewDetailForm={showNewDetailForm}
          setShowNewDetailForm={setShowNewDetailForm}
          isSavingDetail={isSavingDetail}
          onEditHeaderClick={handleEditHeaderClick}
          onVisitHeaderUpdate={handleVisitHeaderUpdate}
          onCancelEditHeader={onCancelEditHeader}
          hasVisitTypeError={hasVisitTypeError}
          onClearVisitTypeError={onClearVisitTypeError}
          handleVisitDetailUpdate={handleVisitDetailUpdate}
          onDeleteDetail={(versionId, visitId, category) =>
            setShowDeleteConfirm({ versionId, visitId, category })
          }
          onDeleteVisit={setShowDeleteVisitConfirm}
          handleSubmitNewDetail={handleSubmitNewDetail}
          onAddDetail={setShowNewDetailForm}
          autoResizeTextarea={autoResizeTextarea}
          availableFiles={availableFiles}
          fieldSuggestions={fieldSuggestions}
          template={template}
          highlightQuery={highlightQuery}
          targetVisitId={targetVisitId}
          onFlashComplete={onFlashComplete}
        />
      )}
      {showBulkEditModal && bulkSelectedCount > 0 && (
        <VisitsBulkEditModal
          selectedVisitIds={Array.from(selectedVisitIds)}
          selectedVisitCount={bulkSelectedCount}
          visits={visits}
          template={template}
          availableFiles={availableFiles}
          fileMapping={fileMapping}
          fieldSuggestions={fieldSuggestions}
          isSaving={isBulkEditingVisits}
          onSave={handleBulkEditSave}
          onClose={() => {
            if (!isBulkEditingVisits) {
              setShowBulkEditModal(false);
            }
          }}
        />
      )}
    </div>
  );
};
